import {
  animate,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { Component, ElementRef, EventEmitter, Input, Output, Renderer2, ViewChild } from '@angular/core';
import { JdOptionComponent } from './jd-option.component';
import {isNotNil} from '../core/convert';

@Component({
  selector           : '[jd-select-top-control]',
  preserveWhitespaces: false,
  animations         : [
    trigger('tagAnimation', [
      state('*', style({ opacity: 1, transform: 'scale(1)' })),
      transition('void => *', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate('150ms linear')
      ]),
      state('void', style({ opacity: 0, transform: 'scale(0)' })),
      transition('* => void', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate('150ms linear')
      ])
    ])
  ],
  templateUrl        : './jd-select-top-control.component.html',
  host               : {
    '[class.ant-select-selection__rendered]': 'true'
  }
})
export class JdSelectTopControlComponent {
  // tslint:disable-next-line:no-any
  private _listOfSelectedValue: any[];
  private _listTemplateOfOption: JdOptionComponent[] = [];
  listOfCachedSelectedOption: JdOptionComponent[] = [];
  inputValue: string;
  isComposing = false;
  @ViewChild('inputElement') inputElement: ElementRef;
  // tslint:disable-next-line:no-any
  @Output() jdListOfSelectedValueChange = new EventEmitter<any[]>();
  @Output() jdOnSearch = new EventEmitter<{ value: string, emit: boolean }>();
  @Input() jdMode = 'default';
  @Input() jdShowSearch = false;
  @Input() jdDisabled = false;

  @Input() jdPlaceHolder: string;
  @Input() jdOpen = false;
  // tslint:disable-next-line:no-any
  @Input() compareWith: (o1: any, o2: any) => boolean;

  @Input()
  // tslint:disable-next-line:no-any
  set jdListOfSelectedValue(value: any[]) {
    this._listOfSelectedValue = value;
    this.updateListOfCachedOption();
  }

  // tslint:disable-next-line:no-any
  get jdListOfSelectedValue(): any[] {
    return this._listOfSelectedValue;
  }

  @Input()
  set jdListTemplateOfOption(value: JdOptionComponent[]) {
    this._listTemplateOfOption = value;
    this.updateListOfCachedOption();
  }

  get jdListTemplateOfOption(): JdOptionComponent[] {
    return this._listTemplateOfOption;
  }

  /** cached selected option list **/
  updateListOfCachedOption(): void {
    if (this.isSingleMode) {
      const selectedOption = this.jdListTemplateOfOption.find(o => this.compareWith(o.jdValue, this.jdListOfSelectedValue[ 0 ]));
      if (isNotNil(selectedOption)) {
        this.listOfCachedSelectedOption = [ selectedOption ];
      }
    } else {
      const listOfCachedOptionFromLatestTemplate = this.jdListTemplateOfOption.filter(o => isNotNil(this.jdListOfSelectedValue.find(v => this.compareWith(v, o.jdValue))));
      const restSelectedValue = this.jdListOfSelectedValue.filter(v => !isNotNil(listOfCachedOptionFromLatestTemplate.find(o => this.compareWith(o.jdValue, v))));
      const listOfCachedOptionFromOld = this.listOfCachedSelectedOption.filter(o => isNotNil(restSelectedValue.find(v => this.compareWith(o.jdValue, v))));
      this.listOfCachedSelectedOption = listOfCachedOptionFromLatestTemplate.concat(listOfCachedOptionFromOld);
    }
  }

  setInputValue(value: string, emit: boolean): void {
    this.inputValue = value;
    this.updateWidth();
    this.jdOnSearch.emit({ value, emit });
  }

  get isSingleMode(): boolean {
    return this.jdMode === 'default';
  }

  get isMultipleOrTags(): boolean {
    return this.jdMode === 'tags' || this.jdMode === 'multiple';
  }

  get placeHolderDisplay(): string {
    return this.inputValue || this.isComposing || this.jdListOfSelectedValue.length ? 'none' : 'block';
  }

  get selectedValueDisplay(): { [ key: string ]: string } {
    let showSelectedValue = false;
    let opacity = 1;
    if (!this.jdShowSearch) {
      showSelectedValue = true;
    } else {
      if (this.jdOpen) {
        showSelectedValue = !(this.inputValue || this.isComposing);
        if (showSelectedValue) {
          opacity = 0.4;
        }
      } else {
        showSelectedValue = true;
      }
    }
    return {
      display: showSelectedValue ? 'block' : 'none',
      opacity: `${opacity}`
    };
  }

  @Input() displayLabel: 'jdLabel' | 'jdValue' = 'jdLabel';

  get singleValueLabel(): string {
    return this.getPropertyFromValue(this.jdListOfSelectedValue[ 0 ], this.displayLabel);
  }

  focusOnInput(): void {
    setTimeout(() => {
      if (this.inputElement) {
        this.inputElement.nativeElement.focus();
      }
    });
  }

  // tslint:disable-next-line:no-any
  getPropertyFromValue(value: any, prop: string): string {
    const targetOption = this.listOfCachedSelectedOption.find(item => this.compareWith(item.jdValue, value));
    return targetOption ? targetOption[ prop ] : '';
  }

  // tslint:disable-next-line:no-any
  isOptionDisplay(value: any): boolean {
    return (this.jdMode === 'tags') || !!this.getPropertyFromValue(value, 'jdLabel');
  }

  // tslint:disable-next-line:no-any
  removeValueFormSelected(value: any): void {
    if (this.jdDisabled || this.getPropertyFromValue(value, 'jdDisabled')) {
      return;
    }
    this._listOfSelectedValue = this.jdListOfSelectedValue.filter(item => item !== value);
    this.jdListOfSelectedValueChange.emit(this.jdListOfSelectedValue);
  }

  updateWidth(): void {
    if (this.isMultipleOrTags && this.inputElement) {
      if (this.inputValue || this.isComposing) {
        this.renderer.setStyle(this.inputElement.nativeElement, 'width', `${this.inputElement.nativeElement.scrollWidth}px`);
      } else {
        this.renderer.removeStyle(this.inputElement.nativeElement, 'width');
      }
    }
  }

  onKeyDownInput(e: KeyboardEvent): void {
    const keyCode = e.keyCode;
    const eventTarget = e.target as HTMLInputElement;
    if (
      this.isMultipleOrTags &&
      !eventTarget.value &&
      // BackSpace
      keyCode === 8
    ) {
      e.preventDefault();
      if (this.jdListOfSelectedValue.length) {
        this.removeValueFormSelected(this.jdListOfSelectedValue[ this.jdListOfSelectedValue.length - 1 ]);
      }
    }
  }

  constructor(private renderer: Renderer2) {

  }
}
